/**
 * RmsDocumentReview
 * -----------------
 * Split-panel review page for AI-generated triage letter paragraphs.
 *
 * This component is the RMS equivalent of the ADHD assessment's RD_CI.js review page.
 * It allows clinicians to review, edit, approve, and ultimately sign-off on the
 * AI-generated paragraphs that will populate the final triage outcome letter
 * (either "Referral Accepted" or "Referral Discharged").
 *
 * Layout:
 *   Left panel (60%):  Scrollable document with colour-coded paragraphs
 *                       (violet = unreviewed, green = approved, amber = editing)
 *   Right panel (40%):  Side panel showing the structured inputs that were fed
 *                       to the AI for the currently selected paragraph
 *
 * Workflow:
 *   1. Clinician clicks a paragraph to select it → side panel shows inputs
 *   2. Floating action panel appears: Edit / Approve / Skip
 *   3. If editing: inline contentEditable with Save / Cancel / Reset
 *   4. Once all paragraphs are approved, "Complete Review" triggers rendering
 *      of the final Google Doc letter template.
 *
 * Data model:
 *   Patient record → triageDocuments.sections.{letterType}.outputs / inputs /
 *   approvedParagraphs / editHistory
 *
 * Firestore path:
 *   rms_organisations/{orgId}/patientRecords/{patientRecordId}
 */

import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import {
 useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { db, auth, functions } from '../../../firebase';
import { doc, getDoc, onSnapshot, runTransaction, serverTimestamp } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import SignOffTriageLetterDialog from '../../components/provider/SignOffTriageLetterDialog';
import ChangeDecisionButton from '../../components/provider/ChangeDecisionButton';
import { triageDecisionChange } from '../../utils/triageDecisionChange';
import ConsentWithdrawnDialog from '../../components/provider/ConsentWithdrawnDialog';
import useConsentWithdrawnStop from '../../hooks/useConsentWithdrawnStop';
import { apiSignOffTriageLetter } from '../../utils/rmsApi';
import { useAuth } from '../../../hooks/useAuth';
import {
  emitLetterParagraphEdited,
  emitLetterParagraphApproved,
  emitLetterParagraphReset,
} from '../../../analytics/rmsLetterEvents';
import { useRmsLetterReviewSession } from '../../../analytics/useReviewSession';
import {
  parseEvidenceLines,
  groupEvidenceLines,
  groupLetterInputs,
  INFORMANT_GROUP_HEADING,
  INPUT_GROUP_DECISIONS,
  INPUT_GROUP_PARENT,
  INPUT_GROUP_SCHOOL,
  INPUT_ROLE_TAGS,
} from '../../utils/llmInputEvidence';
import { apiReviseTriageLetterParagraph } from '../../utils/rmsApi';
import { historyForAnalytics, lastEditType } from '../../utils/letterParagraphHistory';
import {
  describeOverrides,
  evidencePieceKey,
  overrideState,
  splitEvidenceLines,
} from '../../utils/evidenceOverrides';
import { EvidencePieceControls } from '../../components/provider/EvidencePieceControls';
import { ParagraphRevisionPanel } from '../../components/provider/ParagraphRevisionPanel';
import { ParagraphHistory } from '../../components/provider/ParagraphHistory';

import { DocumentReviewHeader } from '../../../pages/providers/11_reviewDocuments/components/DocumentReviewHeader';
import { FloatingActionPanel } from '../../../pages/providers/11_reviewDocuments/components/floatingPanel/FloatingActionPanel';
import { useFloatingActionPanel } from '../../../pages/providers/11_reviewDocuments/components/floatingPanel/useFloatingActionPanel';
import { WarningBar } from '../../../pages/providers/11_reviewDocuments/components/WarningBar';
import { handlePlainTextPaste, suppressFormattingShortcuts } from '../../../pages/providers/11_reviewDocuments/functions/contentEditableUtils';
import { InitialWarningDialog } from '../../../pages/providers/11_reviewDocuments/components/InitialWarningDialog';
import { TriggerRmsDocumentRender } from '../../utils/TriggerRmsDocumentRender';
import RmsTopNavBar from '../../components/provider/RmsTopNavBar';
import ChildIdentityHeader from '../../components/provider/ChildIdentityHeader';
import {
  pathwaysForConcern,
  pathwayTriageDocuments,
  resolveActingPathway,
  triageDocsField,
} from '../../utils/triagePathways';

// Paragraph structure definitions for each letter type, matching the backend
// paragraph registries in rms_prompts.py. The order here determines display order.
const ACCEPTED_DOC_STRUCTURE = {
  'Referral Decision': {
    '1': { title: 'Next Steps', code: 'next_steps' },
  },
};

const DISCHARGED_DOC_STRUCTURE = {
  'Referral Decision': {
    '1': { title: 'Referral Summary', code: 'referral_summary' },
    '2': { title: 'Assessment Findings', code: 'assessment_findings' },
    '3': { title: 'Discharge Rationale', code: 'discharge_rationale' },
    '4': { title: 'Recommendations & Signposting', code: 'recommendations' },
  },
};

// Structured ADHD outcome letter (single letter type covering both accept and
// not-accept). Four sections, reviewed and approved independently; the render is
// gated on all four being approved. The codes are the section keys stored at
// triageDocuments.sections.adhd_outcome_letter.{outputs,inputs,approvedParagraphs}.
const ADHD_DOC_STRUCTURE = {
  'Referral Decision': {
    '1': { title: 'Evidence of ADHD symptoms and impairment', code: 'evidence' },
    '2': { title: 'Other factors', code: 'otherFactors' },
    '3': { title: 'Conclusion and decision', code: 'conclusion' },
    '4': { title: 'Signposting and recommendations', code: 'signposting' },
  },
};

// Autism structured outcome letter — same four section codes as ADHD; only the
// evidence section title differs.
const AUTISM_DOC_STRUCTURE = {
  'Referral Decision': {
    '1': { title: 'Evidence of autism traits and impairment', code: 'evidence' },
    '2': { title: 'Other factors', code: 'otherFactors' },
    '3': { title: 'Conclusion and decision', code: 'conclusion' },
    '4': { title: 'Signposting and recommendations', code: 'signposting' },
  },
};

export default function RmsDocumentReview() {
  const { patientRecordId } = useParams();
  const navigate = useNavigate();
  // The record as the withdrawal watch below sees it (TDI-470 FR5).
  const [withdrawalRecord, setWithdrawalRecord] = useState(null);
  const [withdrawalListenerError, setWithdrawalListenerError] = useState(null);
  const [searchParams] = useSearchParams();
  const pathwayParam = searchParams.get('pathway');

  const { claims } = useAuth();
  const orgId = claims?.rmsOrgId;

  // ─────────────────────────────────────────────────────────────────────────
  // State
  // ─────────────────────────────────────────────────────────────────────────

  // The record itself feeds the shared identity header (TDI-392).
  const [patientData, setPatientData] = useState(null);
  const [triageDocuments, setTriageDocuments] = useState(null);
  const [letterType, setLetterType] = useState(null);
  // The pathway this letter belongs to and the Firestore field holding its
  // documents. For a combined referral each pathway has its own letter under
  // triagePathways.{pathway}.triageDocuments; single-concern uses the top-level
  // triageDocuments. Set once the record loads (see resolveEffPathway).
  const [effPathway, setEffPathway] = useState(null);
  const [docsField, setDocsField] = useState('triageDocuments');
  const [outputs, setOutputs] = useState({});
  const [inputs, setInputs] = useState({});
  const [isLoading, setIsLoading] = useState(true);

  // Access control: only the clinician whose rationale/next-steps fed the letter
  // is permitted to interactively review, edit, and approve the paragraphs.
  const [assignedReviewerId, setAssignedReviewerId] = useState(null);
  const [assignedReviewerName, setAssignedReviewerName] = useState(null);

  // Review workflow state
  const [selectedCode, setSelectedCode] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [reviewedCodes, setReviewedCodes] = useState(new Set());
  const [totalParagraphs, setTotalParagraphs] = useState(0);
  const [isSaving, setIsSaving] = useState(false);

  // Sign-off inside the review (TDI-395): once every paragraph is approved
  // the letter renders while the clinician stays here, and sign-off is the
  // final action of this screen. 'rendering' while the PDF is being built,
  // 'ready' once it exists; null when there is nothing to sign off yet or
  // the clinician has chosen to go back and edit.
  const [signOffStage, setSignOffStage] = useState(null);
  const [showSignOffDialog, setShowSignOffDialog] = useState(false);
  const [signingOff, setSigningOff] = useState(false);
  const [openingLetter, setOpeningLetter] = useState(false);

  // Which section (if any) is currently being regenerated by the AI. Only the
  // structured ADHD outcome letter supports per-section regeneration.
  const [regeneratingCode, setRegeneratingCode] = useState(null);

  // The revision history of every paragraph (TDI-408), as stored at
  // sections.{letterType}.editHistory and kept current with each write this
  // page makes, so the history view never has to re-read the record.
  const [editHistory, setEditHistory] = useState({});

  // An AI revision waiting for the clinician's decision (TDI-404 / TDI-407):
  // { code, mode, text, instruction, baseText, unchanged }. It lives only here
  // until accepted, so the letter never changes without the clinician's say.
  const [proposal, setProposal] = useState(null);
  // 'suggest_changes' | 'polish' while a revision request is in flight.
  const [revisingMode, setRevisingMode] = useState(null);

  // The clinician's adjustments to the evidence behind each paragraph
  // (TDI-406): { [code]: { [pieceKey]: { state, text } } }, stored with the
  // letter at sections.{letterType}.evidenceOverrides so they are there when
  // the clinician returns, and what the redraft is made from.
  const [evidenceOverrides, setEvidenceOverrides] = useState({});

  // Side panel input display state
  const [sidePanelInputs, setSidePanelInputs] = useState(null);

  // Content snapshots for edit/reset
  const [initialContent, setInitialContent] = useState({});
  const [preEditContent, setPreEditContent] = useState({});

  // Warning bar visibility
  const [showWarning, setShowWarning] = useState(true);

  // AI-content acknowledgement modal, shown on entering the review page (mirrors
  // the assessment platform's InitialWarningDialog) so the clinician explicitly
  // accepts responsibility to verify AI-generated content before reviewing.
  const [showInitialWarning, setShowInitialWarning] = useState(true);

  // Refs
  const documentContainerRef = useRef(null);
  const editableContentRef = useRef(null);

  // Resolve which pathway a letter belongs to: combined referrals take it from
  // the URL (?pathway=, carried through the docs flow), else the viewing
  // clinician's own specialism. The old ADHD-first fallback misdirected an
  // autism clinician whose link had dropped the query param into the (often
  // untriaged) ADHD container — their letter looked deleted, approvals wrote
  // to the wrong pathway, and the reviewer gate bounced them.
  const resolveEffPathway = useCallback((intakeConcern) => {
    return (
      resolveActingPathway({ intakeConcern }, pathwayParam, claims?.specialism)
      || pathwaysForConcern(intakeConcern)[0]
      || null
    );
  }, [pathwayParam, claims?.specialism]);

  // Review-sitting measurement (rms_letter_review_session_*). Starts once the
  // letter identity has resolved; noteCompleted is called by handleExitReview
  // when the letter reached full approval, so that sitting ends 'completed'.
  const { noteCompleted: noteReviewCompleted } = useRmsLetterReviewSession({
    patientRecordId,
    letterType,
    pathway: effPathway,
  });

  // Determine document structure based on letter type.
  // Deterministic ADHD letters carry a single `outcome_body` paragraph (plus an
  // optional `next_steps` note on accept), unlike the autism LLM letters which
  // use the fixed accepted/discharged paragraph sets. Detect the ADHD shape by
  // the presence of `outcome_body` in the section outputs.
  const docStructure = useMemo(() => {
    if (outputs && outputs.outcome_body !== undefined) {
      const paragraphs = { '1': { title: 'Triage Outcome', code: 'outcome_body' } };
      if (outputs.next_steps !== undefined && outputs.next_steps !== '') {
        paragraphs['2'] = { title: 'Next Steps', code: 'next_steps' };
      }
      return { 'Referral Decision': paragraphs };
    }
    if (letterType === 'adhd_outcome_letter') return ADHD_DOC_STRUCTURE;
    if (letterType === 'autism_outcome_letter') return AUTISM_DOC_STRUCTURE;
    if (letterType === 'accepted_letter') return ACCEPTED_DOC_STRUCTURE;
    if (letterType === 'discharged_letter') return DISCHARGED_DOC_STRUCTURE;
    return {};
  }, [letterType, outputs]);

  // The structured four-section outcome letter (ADHD or autism) supports
  // per-section regeneration and the all-sections-approved render gate.
  const isStructuredOutcomeLetter =
    letterType === 'adhd_outcome_letter' || letterType === 'autism_outcome_letter';

  // Floating action panel hook — reuses the exact same hook from the ADHD review pages
  const { panelRef, panelPosition, safeUpdatePosition } = useFloatingActionPanel({
    containerRef: documentContainerRef,
    selectedCode,
    isEditing,
    topOffset: -15,
    leftOffset: -275,
  });

  // ─────────────────────────────────────────────────────────────────────────
  // Data Loading
  // ─────────────────────────────────────────────────────────────────────────

  useEffect(() => {
    const loadPatientData = async () => {
      if (!orgId || !patientRecordId) return;

      try {
        setIsLoading(true);
        const patientRef = doc(db, 'rms_organisations', orgId, 'patientRecords', patientRecordId);
        const patientSnap = await getDoc(patientRef);

        if (!patientSnap.exists()) {
          console.error('Patient record not found');
          return;
        }

        const data = patientSnap.data();
        setPatientData(data);

        // Resolve which pathway's letter this review is for, then read that
        // pathway's documents container (combined referrals hold one per
        // pathway; single-concern uses the top-level triageDocuments).
        const eff = resolveEffPathway(data.intakeConcern);
        const df = triageDocsField(data.intakeConcern, eff);
        setEffPathway(eff);
        setDocsField(df);

        const triageDocs = pathwayTriageDocuments(data, eff) || {};
        setTriageDocuments(triageDocs);
        setLetterType(triageDocs.letterType || null);
        setAssignedReviewerId(triageDocs.assignedReviewerId || null);
        // Reopening a review that was approved and rendered but never signed
        // off returns the clinician to where they left off: the sign-off
        // (TDI-395).
        if (triageDocs.reportApproved && triageDocs.documentPaths?.pdf && !triageDocs.signOff) {
          setSignOffStage('ready');
        }
        setAssignedReviewerName(triageDocs.assignedReviewerName || null);

        // Load section data for the determined letter type
        const sectionData = triageDocs.sections?.[triageDocs.letterType] || {};
        setOutputs(sectionData.outputs || {});
        setInputs(sectionData.inputs || {});
        setEditHistory(sectionData.editHistory || {});
        setEvidenceOverrides(sectionData.evidenceOverrides || {});

        // Initialise initial content snapshot (for reset functionality)
        setInitialContent({ ...(sectionData.outputs || {}) });

        // Load previously approved paragraphs
        const approved = sectionData.approvedParagraphs || {};
        setReviewedCodes(new Set(Object.keys(approved)));
      } catch (error) {
        console.error('Error loading patient data:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadPatientData();
  }, [orgId, patientRecordId, resolveEffPathway]);

  // TDI-396: while the drafts are being written, each paragraph is saved the
  // moment it is written, so watch the record and let the review fill in
  // live instead of waiting for the slowest paragraph. The watch ends when
  // the generation reports complete (or fails).
  const processingStatus = triageDocuments?.processingStatus || null;
  useEffect(() => {
    if (processingStatus !== 'processing' || !orgId || !patientRecordId) return undefined;
    const patientRef = doc(db, 'rms_organisations', orgId, 'patientRecords', patientRecordId);
    return onSnapshot(patientRef, (snap) => {
      if (!snap.exists()) return;
      const data = snap.data() || {};
      const docs = pathwayTriageDocuments(data, effPathway) || {};
      const resolvedType = docs.letterType || letterType;
      const section = (docs.sections || {})[resolvedType] || {};
      setTriageDocuments(docs);
      if (docs.letterType) setLetterType(docs.letterType);
      setOutputs((prev) => ({ ...prev, ...(section.outputs || {}) }));
      setInputs((prev) => ({ ...prev, ...(section.inputs || {}) }));
    });
  }, [processingStatus, orgId, patientRecordId, effPathway, letterType]);

  // Count total paragraphs that have generated content
  useEffect(() => {
    const total = Object.values(docStructure).reduce((acc, section) => {
      const available = Object.values(section).filter(
        ({ code }) => outputs[code] !== undefined && outputs[code] !== ''
      );
      return acc + available.length;
    }, 0);
    setTotalParagraphs(total);
  }, [docStructure, outputs]);

  // The codes that are actually reviewable paragraphs in the current structure
  // (non-empty content). Deterministic ADHD letters auto-approve EVERY engine
  // output key — including variable placeholders (reply_by_date, etc.) that are
  // not paragraphs — so `reviewedCodes` can exceed the paragraph set. Intersect
  // to keep counts/gates correct (otherwise "left to review" goes negative and
  // the completion gate never unlocks).
  const validParagraphCodes = useMemo(() => {
    const codes = new Set();
    Object.values(docStructure).forEach((section) =>
      Object.values(section).forEach(({ code }) => {
        if (outputs[code] !== undefined && outputs[code] !== '') codes.add(code);
      })
    );
    return codes;
  }, [docStructure, outputs]);

  const effectiveReviewed = useMemo(
    () => new Set([...reviewedCodes].filter((c) => validParagraphCodes.has(c))),
    [reviewedCodes, validParagraphCodes]
  );

  // Update warning visibility when review progress changes
  useEffect(() => {
    setShowWarning(totalParagraphs > effectiveReviewed.size);
  }, [effectiveReviewed.size, totalParagraphs]);

  // Lock body scroll while the review page is mounted
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  // ─────────────────────────────────────────────────────────────────────────
  // Progress Calculation
  // ─────────────────────────────────────────────────────────────────────────

  const calculateProgress = useCallback(() => {
    if (totalParagraphs === 0) return 0;
    return Math.round((effectiveReviewed.size / totalParagraphs) * 100);
  }, [effectiveReviewed.size, totalParagraphs]);

  // ─────────────────────────────────────────────────────────────────────────
  // Paragraph Click Handler
  // ─────────────────────────────────────────────────────────────────────────

  // TD-249: a signed-off letter is final — the review surface goes read-only.
  // Paragraphs can still be selected to read their inputs and history, but
  // the Edit / Approve / Reset panel, regenerate and revision controls are
  // hidden; the backend blocks the writes as well.
  const isSignedOff = Boolean(triageDocuments?.signOff);

  const handleParagraphClick = useCallback((event, code, content) => {
    if (isEditing) return;

    // A signed-off letter can still be read paragraph by paragraph: the
    // inputs and the history (TDI-408) are part of the audit trail. The edit,
    // approve, regenerate and revision controls stay hidden.
    setSelectedCode(code);

    // Populate side panel with the structured inputs for this paragraph
    if (inputs[code]) {
      setSidePanelInputs(inputs[code]);
    } else {
      setSidePanelInputs(null);
    }

    setTimeout(() => safeUpdatePosition(code), 0);
  }, [isEditing, isSignedOff, inputs, safeUpdatePosition]);

  // Deselect paragraph when clicking outside of paragraphs/action panel
  const handlePageClick = useCallback((event) => {
    if (isEditing) return;
    if (
      event.target.closest('[data-paragraph-container]') ||
      event.target.closest('[data-action-panel]')
    ) return;

    setIsEditing(false);
    setSelectedCode(null);
    setSidePanelInputs(null);
  }, [isEditing]);

  useEffect(() => {
    const container = documentContainerRef.current;
    if (container) container.addEventListener('click', handlePageClick);
    return () => { if (container) container.removeEventListener('click', handlePageClick); };
  }, [handlePageClick]);

  // ─────────────────────────────────────────────────────────────────────────
  // Edit Handlers
  // ─────────────────────────────────────────────────────────────────────────

  const handleStartEdit = useCallback(() => {
    if (!selectedCode) return;

    // Snapshot the content before this edit session for cancel/revert
    setPreEditContent((prev) => ({
      ...prev,
      [selectedCode]: outputs[selectedCode] || '',
    }));
    setIsEditing(true);

    setTimeout(() => {
      const el = document.querySelector(`#${CSS.escape(selectedCode)} p`);
      if (el) {
        el.focus();
        const range = document.createRange();
        const sel = window.getSelection();
        range.selectNodeContents(el);
        range.collapse(false);
        sel.removeAllRanges();
        sel.addRange(range);
      }
      safeUpdatePosition(selectedCode);
    }, 0);
  }, [selectedCode, outputs, safeUpdatePosition]);

  // Read the live edited text for a paragraph straight from its contentEditable
  // DOM node (the source of truth while editing, since we no longer mirror each
  // keystroke into React state — see the onInput comment). Falls back to the
  // stored output when the node isn't found.
  const readEditedContent = useCallback(
    (code) => {
      if (!code) return '';
      const el = document.querySelector(`#${CSS.escape(code)} p`);
      // innerText keeps the line breaks the clinician typed; textContent is
      // the fallback where a DOM has no innerText (jsdom).
      return el ? (el.innerText ?? el.textContent) : (outputs[code] || '');
    },
    [outputs],
  );

  // Keep the on-page history in step with an entry this page has just written.
  // The record holds a server timestamp; the page shows the moment it wrote.
  const rememberHistoryEntry = useCallback((code, key, entry) => {
    setEditHistory((prev) => ({
      ...prev,
      [code]: { ...(prev[code] || {}), [key]: { ...entry, timestamp: new Date() } },
    }));
  }, []);

  const handleSaveEdit = useCallback(async () => {
    if (!selectedCode) return;

    const newValue = readEditedContent(selectedCode);
    const previousValue = initialContent[selectedCode] || '';

    if (newValue !== previousValue) {
      // Commit the edited text to state so the rendered paragraph reflects it
      // after leaving edit mode.
      setOutputs((prev) => ({ ...prev, [selectedCode]: newValue }));
      // Optimistic: remove from approved, exit edit mode
      setReviewedCodes((prev) => {
        const next = new Set(prev);
        next.delete(selectedCode);
        return next;
      });
      setIsEditing(false);
      safeUpdatePosition(selectedCode);

      const editEntry = {
        // Optional-chain: auth.currentUser can be momentarily null during a
        // token refresh; a hard deref here crashed the page mid-edit.
        clinicianId: auth.currentUser?.uid || null,
        clinicianName: auth.currentUser?.displayName || null,
        timestamp: serverTimestamp(),
        previousValue,
        newValue,
        editType: 'manual_edit',
      };
      const timestampKey = Date.now().toString();

      // The paragraph's history BEFORE this edit, captured inside the
      // transaction that already reads it. It is what lets the adoption numbers
      // be computed at the moment of the edit rather than by reading the letter
      // back out of Firestore: after a second edit, the text being replaced is
      // the clinician's own earlier wording, not the AI's draft.
      let priorHistoryForCode;
      let writtenEntry = editEntry;
      try {
        const patientRef = doc(db, 'rms_organisations', orgId, 'patientRecords', patientRecordId);
        await runTransaction(db, async (transaction) => {
          const snap = await transaction.get(patientRef);
          if (!snap.exists()) throw new Error('Patient record does not exist');

          const data = snap.data();
          const section = pathwayTriageDocuments(data, effPathway)?.sections?.[letterType] || {};
          const currentApproved = section.approvedParagraphs || {};
          const currentHistory = section.editHistory || {};

          // Captured before this edit is appended below, and re-captured on a
          // transaction retry so the committed attempt is the one that counts.
          // Approvals are left out: they are not changes of text.
          priorHistoryForCode = historyForAnalytics(currentHistory[selectedCode] || {});

          // The history records the un-approval on the change that caused it.
          writtenEntry = currentApproved[selectedCode]
            ? { ...editEntry, clearedApproval: true }
            : editEntry;
          delete currentApproved[selectedCode];
          if (!currentHistory[selectedCode]) currentHistory[selectedCode] = {};
          currentHistory[selectedCode][timestampKey] = writtenEntry;

          transaction.update(patientRef, {
            [`${docsField}.sections.${letterType}.approvedParagraphs`]: currentApproved,
            [`${docsField}.sections.${letterType}.editHistory`]: currentHistory,
            [`${docsField}.sections.${letterType}.outputs.${selectedCode}`]: newValue,
            [`${docsField}.sections.${letterType}.lastReviewedAt`]: serverTimestamp(),
            [`${docsField}.sections.${letterType}.reviewedBy`]: auth.currentUser.uid,
            [`${docsField}.sections.${letterType}.reviewedByName`]: auth.currentUser.displayName,
          });
        });
        rememberHistoryEntry(selectedCode, timestampKey, writtenEntry);

        // After the commit: an event must not claim a change that did not land.
        emitLetterParagraphEdited({
          patientRecordId,
          letterType,
          pathway: effPathway,
          code: selectedCode,
          prevText: previousValue,
          nextText: newValue,
          priorHistory: priorHistoryForCode,
        });
      } catch (error) {
        console.error('Error saving edit:', error);
        toast.error('Your edit could not be saved. Please try again.');
        // Roll back optimistic update
        setReviewedCodes((prev) => new Set([...prev, selectedCode]));
      }
    } else {
      setIsEditing(false);
      safeUpdatePosition(selectedCode);
    }
  }, [selectedCode, readEditedContent, initialContent, orgId, patientRecordId, letterType, docsField, effPathway, safeUpdatePosition, rememberHistoryEntry]);

  const handleCancel = useCallback(() => {
    if (selectedCode && preEditContent[selectedCode] !== undefined) {
      setOutputs((prev) => ({ ...prev, [selectedCode]: preEditContent[selectedCode] }));
    }
    setIsEditing(false);
    safeUpdatePosition(selectedCode);
  }, [selectedCode, preEditContent, safeUpdatePosition]);

  const handleReset = useCallback(async () => {
    if (!selectedCode || !initialContent[selectedCode]) return;

    const newValue = initialContent[selectedCode];
    const previousValue = outputs[selectedCode];

    // Optimistic update
    setOutputs((prev) => ({ ...prev, [selectedCode]: newValue }));
    setReviewedCodes((prev) => {
      const next = new Set(prev);
      next.delete(selectedCode);
      return next;
    });

    const editEntry = {
      // Optional-chain: auth.currentUser can be momentarily null during a
      // token refresh; a hard deref here crashed the page mid-reset.
      clinicianId: auth.currentUser?.uid || null,
      clinicianName: auth.currentUser?.displayName || null,
      timestamp: serverTimestamp(),
      previousValue,
      newValue,
      editType: 'reset',
    };
    const timestampKey = Date.now().toString();
    let writtenEntry = editEntry;

    try {
      const patientRef = doc(db, 'rms_organisations', orgId, 'patientRecords', patientRecordId);
      await runTransaction(db, async (transaction) => {
        const snap = await transaction.get(patientRef);
        if (!snap.exists()) throw new Error('Patient record does not exist');

        const data = snap.data();
        const section = pathwayTriageDocuments(data, effPathway)?.sections?.[letterType] || {};
        const currentApproved = section.approvedParagraphs || {};
        const currentHistory = section.editHistory || {};

        writtenEntry = currentApproved[selectedCode]
          ? { ...editEntry, clearedApproval: true }
          : editEntry;
        delete currentApproved[selectedCode];
        if (!currentHistory[selectedCode]) currentHistory[selectedCode] = {};
        currentHistory[selectedCode][timestampKey] = writtenEntry;

        transaction.update(patientRef, {
          [`${docsField}.sections.${letterType}.approvedParagraphs`]: currentApproved,
          [`${docsField}.sections.${letterType}.editHistory`]: currentHistory,
          [`${docsField}.sections.${letterType}.outputs.${selectedCode}`]: newValue,
          [`${docsField}.sections.${letterType}.lastReviewedAt`]: serverTimestamp(),
          [`${docsField}.sections.${letterType}.reviewedBy`]: auth.currentUser.uid,
          [`${docsField}.sections.${letterType}.reviewedByName`]: auth.currentUser.displayName,
        });
      });
      rememberHistoryEntry(selectedCode, timestampKey, writtenEntry);

      // After the commit, for the same reason as the edit above.
      emitLetterParagraphReset({
        patientRecordId,
        letterType,
        pathway: effPathway,
        code: selectedCode,
      });
    } catch (error) {
      console.error('Error resetting paragraph:', error);
      toast.error('The paragraph could not be reset. Please try again.');
      setOutputs((prev) => ({ ...prev, [selectedCode]: previousValue }));
      setReviewedCodes((prev) => new Set([...prev, selectedCode]));
    }

    setIsEditing(false);
    safeUpdatePosition(selectedCode);
  }, [selectedCode, initialContent, outputs, orgId, patientRecordId, letterType, docsField, effPathway, safeUpdatePosition, rememberHistoryEntry]);

  const hasBeenModified = useCallback((code) => {
    if (!code) return false;
    return outputs[code] !== initialContent[code];
  }, [outputs, initialContent]);

  // ─────────────────────────────────────────────────────────────────────────
  // Navigation Helpers (defined before approval handler which calls them)
  // ─────────────────────────────────────────────────────────────────────────

  const advanceToNextUnreviewed = useCallback((currentCode) => {
    const allParagraphs = document.querySelectorAll('[data-paragraph-container]');
    const currentIndex = Array.from(allParagraphs).findIndex((p) => p.id === currentCode);
    const nextUnreviewed = Array.from(allParagraphs).find(
      (p, idx) => idx > currentIndex && !reviewedCodes.has(p.id)
    );

    if (nextUnreviewed && documentContainerRef.current) {
      const containerRect = documentContainerRef.current.getBoundingClientRect();
      const nextRect = nextUnreviewed.getBoundingClientRect();
      const scrollTop =
        documentContainerRef.current.scrollTop +
        (nextRect.top - containerRect.top) -
        containerRect.height / 3;

      documentContainerRef.current.scrollTo({ top: scrollTop, behavior: 'smooth' });

      setTimeout(() => {
        const nextCode = nextUnreviewed.id;
        handleParagraphClick({ currentTarget: nextUnreviewed }, nextCode, outputs[nextCode]);
      }, 300);
    } else {
      setSelectedCode(null);
      setSidePanelInputs(null);
    }
  }, [reviewedCodes, outputs, handleParagraphClick]);

  const goToFirstUnreviewed = useCallback(() => {
    const allParagraphs = document.querySelectorAll('[data-paragraph-container]');
    const firstUnreviewed = Array.from(allParagraphs).find((p) => !reviewedCodes.has(p.id));

    if (firstUnreviewed && documentContainerRef.current) {
      const containerRect = documentContainerRef.current.getBoundingClientRect();
      const nextRect = firstUnreviewed.getBoundingClientRect();
      const scrollTop =
        documentContainerRef.current.scrollTop +
        (nextRect.top - containerRect.top) -
        containerRect.height / 3;

      documentContainerRef.current.scrollTo({ top: scrollTop, behavior: 'smooth' });

      setTimeout(() => {
        const nextCode = firstUnreviewed.id;
        handleParagraphClick({ currentTarget: firstUnreviewed }, nextCode, outputs[nextCode]);
      }, 300);
    }
  }, [reviewedCodes, outputs, handleParagraphClick]);

  // ─────────────────────────────────────────────────────────────────────────
  // Approval Handler
  // ─────────────────────────────────────────────────────────────────────────

  const handleApprove = useCallback(async () => {
    if (!selectedCode) return;

    // Optimistic UI update
    setReviewedCodes((prev) => new Set([...prev, selectedCode]));

    const approvedText = readEditedContent(selectedCode);
    // The approval goes into the paragraph's history too (TDI-408): the
    // approvedParagraphs entry is overwritten by the next approval, the
    // history keeps every one with who, when and the text approved.
    const approvalEntry = {
      clinicianId: auth.currentUser?.uid || null,
      clinicianName: auth.currentUser?.displayName || null,
      timestamp: serverTimestamp(),
      editType: 'approved',
      approvedText,
    };
    const timestampKey = Date.now().toString();

    try {
      const patientRef = doc(db, 'rms_organisations', orgId, 'patientRecords', patientRecordId);
      await runTransaction(db, async (transaction) => {
        const snap = await transaction.get(patientRef);
        if (!snap.exists()) throw new Error('Patient record does not exist');

        const data = snap.data();
        const section = pathwayTriageDocuments(data, effPathway)?.sections?.[letterType] || {};
        const currentApproved = section.approvedParagraphs || {};
        const currentHistory = section.editHistory || {};

        currentApproved[selectedCode] = {
          approvedAt: serverTimestamp(),
          approvedBy: auth.currentUser.uid,
          approvedByName: auth.currentUser.displayName,
          content: approvedText,
        };
        if (!currentHistory[selectedCode]) currentHistory[selectedCode] = {};
        currentHistory[selectedCode][timestampKey] = approvalEntry;

        transaction.update(patientRef, {
          [`${docsField}.sections.${letterType}.approvedParagraphs`]: currentApproved,
          [`${docsField}.sections.${letterType}.editHistory`]: currentHistory,
          [`${docsField}.sections.${letterType}.lastReviewedAt`]: serverTimestamp(),
          [`${docsField}.sections.${letterType}.reviewedBy`]: auth.currentUser.uid,
          [`${docsField}.sections.${letterType}.reviewedByName`]: auth.currentUser.displayName,
        });
      });
      rememberHistoryEntry(selectedCode, timestampKey, approvalEntry);

      // auto_approved is false here by definition: a clinician pressed approve.
      // The flag exists so that paragraphs marked approved by generation, which
      // nobody read, can never be mixed into an adoption rate.
      emitLetterParagraphApproved({
        patientRecordId,
        letterType,
        pathway: effPathway,
        code: selectedCode,
        autoApproved: false,
      });
    } catch (error) {
      console.error('Error approving paragraph:', error);
      // Surface the failure — silently reverting the checkmark made a rejected
      // write look like the approval randomly "not saving".
      toast.error('The approval could not be saved. Please try again.');
      setReviewedCodes((prev) => {
        const next = new Set(prev);
        next.delete(selectedCode);
        return next;
      });
    }

    // Auto-advance to next unreviewed paragraph
    advanceToNextUnreviewed(selectedCode);
  }, [selectedCode, orgId, patientRecordId, letterType, docsField, effPathway, readEditedContent, advanceToNextUnreviewed, rememberHistoryEntry]);

  const handleSkip = useCallback(() => {
    advanceToNextUnreviewed(selectedCode);
  }, [selectedCode, advanceToNextUnreviewed]);

  // ─────────────────────────────────────────────────────────────────────────
  // Regenerate (ADHD outcome letter only)
  // ─────────────────────────────────────────────────────────────────────────
  // Re-runs generation for a single section. The Cloud Function rewrites that
  // section in place and clears its approval, so on return we re-read the
  // section from Firestore and refresh the local paragraph as unapproved.

  const handleRegenerate = useCallback(async () => {
    if (!selectedCode || regeneratingCode) return;

    const code = selectedCode;
    setRegeneratingCode(code);
    // Exit any in-progress edit so the regenerated text is what is shown.
    setIsEditing(false);

    try {
      const generate = httpsCallable(functions, 'rmsGenerateTriageLetter', { timeout: 300000 });
      await generate({ orgId, patientRecordId, ...(effPathway ? { pathway: effPathway } : {}), sectionKey: code });

      // The document is updated in place — re-read the section to pick up the
      // regenerated text, refreshed inputs, and the cleared approval state.
      const patientRef = doc(db, 'rms_organisations', orgId, 'patientRecords', patientRecordId);
      const snap = await getDoc(patientRef);
      if (snap.exists()) {
        const section = pathwayTriageDocuments(snap.data(), effPathway)?.sections?.[letterType] || {};
        const newOutputs = section.outputs || {};
        const newInputs = section.inputs || {};
        const approved = section.approvedParagraphs || {};

        const newText = newOutputs[code] ?? '';
        setOutputs((prev) => ({ ...prev, [code]: newText }));
        setInputs((prev) => ({ ...prev, [code]: newInputs[code] }));
        // Reset the edit/undo baseline to the freshly generated text.
        setInitialContent((prev) => ({ ...prev, [code]: newText }));
        // Reflect the backend having cleared this section's approval.
        setReviewedCodes(new Set(Object.keys(approved)));
        // The server wrote the regeneration into the history.
        setEditHistory(section.editHistory || {});
        // A proposal made against the old text no longer applies.
        setProposal((prev) => (prev && prev.code === code ? null : prev));

        // Keep the side panel in sync if this section is still selected.
        setSidePanelInputs(newInputs[code] || null);
      }
    } catch (error) {
      console.error('Error regenerating section:', error);
      toast.error('The section could not be regenerated. Please try again.');
    } finally {
      setRegeneratingCode(null);
    }
  }, [selectedCode, regeneratingCode, orgId, patientRecordId, letterType, effPathway]);

  // ─────────────────────────────────────────────────────────────────────────
  // AI revision of the selected paragraph (TDI-404 suggest changes,
  // TDI-407 polish my edits)
  // ─────────────────────────────────────────────────────────────────────────
  // The callable returns a proposal and writes nothing. The clinician reads it
  // beside the current paragraph and either uses it (recorded in editHistory
  // with the mode and instruction, approval cleared) or keeps the current text.

  const requestRevision = useCallback(async (mode, instruction = '') => {
    if (!selectedCode || revisingMode || isEditing || isSignedOff) return;

    const code = selectedCode;
    const baseText = outputs[code] || '';
    setRevisingMode(mode);
    try {
      const result = await apiReviseTriageLetterParagraph({
        orgId,
        patientRecordId,
        pathway: effPathway,
        sectionKey: code,
        mode,
        instruction,
        currentText: baseText,
      });
      setProposal({
        code,
        mode,
        text: result?.proposal || '',
        instruction,
        baseText,
        unchanged: Boolean(result?.unchanged),
        // The evidence the redraft was made from, kept with the proposal so
        // accepting it records exactly those adjustments (TDI-406).
        ...(mode === 'evidence_change'
          ? { overridesSnapshot: { ...(evidenceOverrides[code] || {}) } }
          : {}),
      });
    } catch (error) {
      console.error('Error revising paragraph:', error);
      toast.error(error?.message || 'The paragraph could not be revised. Please try again.');
    } finally {
      setRevisingMode(null);
    }
  }, [selectedCode, revisingMode, isEditing, isSignedOff, outputs, orgId, patientRecordId, effPathway, evidenceOverrides]);

  const handleSuggestChanges = useCallback(
    (instruction) => requestRevision('suggest_changes', instruction),
    [requestRevision],
  );

  const handlePolish = useCallback(() => requestRevision('polish'), [requestRevision]);

  const handleDiscardProposal = useCallback(() => setProposal(null), []);

  const handleAcceptProposal = useCallback(async () => {
    if (!proposal || proposal.unchanged) return;

    const { code, mode, text: newValue, instruction, baseText } = proposal;
    const previousValue = outputs[code] || '';
    setProposal(null);

    // The proposal was made against the paragraph as it then read. If the
    // text has moved on since, the proposal no longer describes a change the
    // clinician has read in context, so it is not applied.
    if (previousValue !== baseText) {
      toast.error('The paragraph changed since this was suggested. Please ask again.');
      return;
    }

    const wasApproved = reviewedCodes.has(code);
    setOutputs((prev) => ({ ...prev, [code]: newValue }));
    setReviewedCodes((prev) => {
      const next = new Set(prev);
      next.delete(code);
      return next;
    });

    const editEntry = {
      clinicianId: auth.currentUser?.uid || null,
      clinicianName: auth.currentUser?.displayName || null,
      timestamp: serverTimestamp(),
      previousValue,
      newValue,
      editType: mode,
      ...(mode === 'suggest_changes' ? { instruction } : {}),
      ...(mode === 'evidence_change' ? { evidenceOverrides: proposal.overridesSnapshot || {} } : {}),
    };
    const timestampKey = Date.now().toString();
    let writtenEntry = editEntry;

    try {
      const patientRef = doc(db, 'rms_organisations', orgId, 'patientRecords', patientRecordId);
      await runTransaction(db, async (transaction) => {
        const snap = await transaction.get(patientRef);
        if (!snap.exists()) throw new Error('Patient record does not exist');

        const data = snap.data();
        const section = pathwayTriageDocuments(data, effPathway)?.sections?.[letterType] || {};
        const currentApproved = section.approvedParagraphs || {};
        const currentHistory = section.editHistory || {};

        writtenEntry = currentApproved[code] ? { ...editEntry, clearedApproval: true } : editEntry;
        delete currentApproved[code];
        if (!currentHistory[code]) currentHistory[code] = {};
        currentHistory[code][timestampKey] = writtenEntry;

        transaction.update(patientRef, {
          [`${docsField}.sections.${letterType}.approvedParagraphs`]: currentApproved,
          [`${docsField}.sections.${letterType}.editHistory`]: currentHistory,
          [`${docsField}.sections.${letterType}.outputs.${code}`]: newValue,
          [`${docsField}.sections.${letterType}.lastReviewedAt`]: serverTimestamp(),
          [`${docsField}.sections.${letterType}.reviewedBy`]: auth.currentUser.uid,
          [`${docsField}.sections.${letterType}.reviewedByName`]: auth.currentUser.displayName,
        });
      });
      rememberHistoryEntry(code, timestampKey, writtenEntry);
      // No analytics event yet: the event registry (analytics/events.schema.json,
      // shared with the assessment platform) has no revision event and its
      // edit_type enum is closed. Adding one is a registry change of its own.
    } catch (error) {
      console.error('Error applying revision:', error);
      toast.error('The revision could not be saved. Please try again.');
      setOutputs((prev) => ({ ...prev, [code]: previousValue }));
      if (wasApproved) setReviewedCodes((prev) => new Set([...prev, code]));
    }
  }, [proposal, outputs, reviewedCodes, orgId, patientRecordId, letterType, docsField, effPathway, rememberHistoryEntry]);

  // "Polish my edits" is offered once the clinician's own edit is the latest
  // change to the paragraph; it tidies their wording, not the AI's.
  // ── Evidence overrides on the selected paragraph (TDI-406) ──────────────
  // A choice is saved the moment it is made, so it is there when the
  // clinician returns; the paragraph itself only changes when they ask for
  // the redraft and accept it.
  const handleSetEvidenceOverride = useCallback(async (code, key, text, state) => {
    if (!code || isSignedOff) return;
    const before = evidenceOverrides[code] || {};
    const next = { ...before };
    if (state) next[key] = { state, text };
    else delete next[key];

    setEvidenceOverrides((prev) => ({ ...prev, [code]: next }));
    // A redraft proposed from the old adjustments no longer describes these.
    setProposal((prev) => (prev && prev.code === code && prev.mode === 'evidence_change' ? null : prev));

    try {
      const patientRef = doc(db, 'rms_organisations', orgId, 'patientRecords', patientRecordId);
      await runTransaction(db, async (transaction) => {
        const snap = await transaction.get(patientRef);
        if (!snap.exists()) throw new Error('Patient record does not exist');

        const data = snap.data();
        const section = pathwayTriageDocuments(data, effPathway)?.sections?.[letterType] || {};
        const current = section.evidenceOverrides || {};
        if (Object.keys(next).length) current[code] = next;
        else delete current[code];

        transaction.update(patientRef, {
          [`${docsField}.sections.${letterType}.evidenceOverrides`]: current,
        });
      });
    } catch (error) {
      console.error('Error saving evidence adjustment:', error);
      toast.error('The evidence adjustment could not be saved. Please try again.');
      setEvidenceOverrides((prev) => ({ ...prev, [code]: before }));
    }
  }, [evidenceOverrides, isSignedOff, orgId, patientRecordId, letterType, docsField, effPathway]);

  const handleRegenerateFromEvidence = useCallback(
    () => requestRevision('evidence_change'),
    [requestRevision],
  );

  // Deterministic ADHD letters are not drafted from evidence, so there is
  // nothing to adjust; every other letter type has a drafting prompt per
  // paragraph that the redraft reuses.
  const canAdjustEvidence = Boolean(
    selectedCode && !isEditing && !isSignedOff && outputs.outcome_body === undefined,
  );
  const selectedOverrides = selectedCode ? (evidenceOverrides[selectedCode] || {}) : {};
  const evidenceAdjustedText = describeOverrides(selectedOverrides);

  const canPolishSelected = Boolean(
    selectedCode && lastEditType(editHistory[selectedCode]) === 'manual_edit',
  );

  // The review-panel title of each paragraph, for the history heading.
  const paragraphTitles = useMemo(() => {
    const titles = {};
    Object.values(docStructure).forEach((section) =>
      Object.values(section).forEach(({ code, title }) => { titles[code] = title; })
    );
    return titles;
  }, [docStructure]);

  // ─────────────────────────────────────────────────────────────────────────
  // Save & Exit / Complete Review
  // ─────────────────────────────────────────────────────────────────────────

  const signOffStageRef = useRef(null);
  useEffect(() => { signOffStageRef.current = signOffStage; }, [signOffStage]);
  const renderStartedRef = useRef(false);

  const handleExitReview = useCallback(async () => {
    // Once the letter is being prepared, both header buttons ("Complete
    // Review" and "Save & Exit") lead to the sign-off rather than a second
    // render: at a staging run-through (2 Nov) three clicks in 30 seconds
    // started three renders while the first was still on its way.
    if (signOffStageRef.current === 'rendering') return;
    if (signOffStageRef.current === 'ready') {
      setShowSignOffDialog(true);
      return;
    }
    setIsSaving(true);
    try {
      const isFullyApproved = totalParagraphs > 0 && totalParagraphs === effectiveReviewed.size;

      console.log('[RmsDocumentReview] handleExitReview:', {
        totalParagraphs,
        reviewedCount: effectiveReviewed.size,
        isFullyApproved,
        orgId,
        patientRecordId,
      });

      const patientRef = doc(db, 'rms_organisations', orgId, 'patientRecords', patientRecordId);
      await runTransaction(db, async (transaction) => {
        const snap = await transaction.get(patientRef);
        if (!snap.exists()) throw new Error('Patient record does not exist');

        transaction.update(patientRef, {
          [`${docsField}.reportApproved`]: isFullyApproved,
          [`${docsField}.reportApprovedBy`]: isFullyApproved ? auth.currentUser.uid : null,
          [`${docsField}.reportApprovedByName`]: isFullyApproved ? auth.currentUser.displayName : null,
          [`${docsField}.reportApprovedAt`]: isFullyApproved ? serverTimestamp() : null,
        });
      });

      if (isFullyApproved) {
        // The letter reached full approval during this sitting, so the
        // review-session row that ends on the navigation below reads
        // 'completed' rather than 'navigated'.
        noteReviewCompleted();
        console.log('[RmsDocumentReview] Triggering document render...');
        // Pass the pathway so the render resolves the right letter on combined
        // (ASD+ADHD) referrals. The ADHD outcome letter implies the adhd
        // pathway; otherwise fall back to the pathway carried in the URL.
        TriggerRmsDocumentRender({ orgId, patientRecordId, ...(effPathway ? { pathway: effPathway } : {}) });
      }

      // Sign-off is the final action of this review, not a separate errand
      // on another page (Tom King, 29 Sept: "build the sign off step into
      // reviewing the AI outputs to de-risk it"; TDI-395: "no round trip
      // through the Documents page"). On full approval the clinician stays
      // here while the letter renders; the sign-off opens as soon as the PDF
      // exists, because sign-off attests to the FINAL RENDERED letter. "Go
      // back and edit" is the only other way out of it.
      if (isFullyApproved) {
        renderStartedRef.current = false;
        setSignOffStage('rendering');
        return;
      }
      const params = new URLSearchParams();
      if (pathwayParam) params.set('pathway', pathwayParam);
      const query = params.toString();
      navigate(`/rms/provider/patient/${patientRecordId}/documents${query ? `?${query}` : ''}`);
    } catch (error) {
      console.error('Error during exit review:', error);
      toast.error('Your review progress could not be saved. Please try again.');
    } finally {
      setIsSaving(false);
    }
  }, [totalParagraphs, effectiveReviewed.size, orgId, patientRecordId, navigate, docsField, effPathway, noteReviewCompleted]);

  // ── Sign-off inside the review (TDI-395) ──────────────────────────────────
  // Only the clinician who reviewed the paragraphs signs the letter off; the
  // server refuses anyone else (rmsSignOffTriageLetter).
  const canSignOffHere = !assignedReviewerId || assignedReviewerId === auth.currentUser?.uid;
  const retryRender = useCallback(() => {
    renderStartedRef.current = false;
    setSignOffStage('rendering');
    TriggerRmsDocumentRender({ orgId, patientRecordId, ...(effPathway ? { pathway: effPathway } : {}) });
  }, [orgId, patientRecordId, effPathway]);

  // While a sign-off is pending, watch the record: the render lands the PDF
  // (rendering becomes ready and the sign-off opens), and a sign-off given
  // elsewhere locks this screen.
  useEffect(() => {
    if (!signOffStage || !orgId || !patientRecordId) return undefined;
    const patientRef = doc(db, 'rms_organisations', orgId, 'patientRecords', patientRecordId);
    return onSnapshot(patientRef, (snap) => {
      if (!snap.exists()) return;
      const data = snap.data() || {};
      const docs = docsField.split('.').reduce((node, part) => (node ? node[part] : undefined), data) || {};
      if (docs.signOff) {
        setTriageDocuments((prev) => ({ ...(prev || {}), signOff: docs.signOff }));
        setShowSignOffDialog(false);
        setSignOffStage(null);
        return;
      }
      if (docs.documentPaths?.pdf && signOffStageRef.current === 'rendering' && docs.processingStatus !== 'processing') {
        setSignOffStage('ready');
        setShowSignOffDialog(true);
        return;
      }
      // A render that fails says so instead of leaving "being prepared" up
      // for good. Only an error seen after this render started counts, not
      // one left over from an earlier attempt.
      if (signOffStageRef.current === 'rendering') {
        if (docs.processingStatus === 'processing') renderStartedRef.current = true;
        else if (docs.processingStatus === 'error' && renderStartedRef.current) setSignOffStage('failed');
      }
    });
  }, [signOffStage, orgId, patientRecordId, docsField]);

  const handleOpenLetter = useCallback(async () => {
    if (openingLetter) return;
    setOpeningLetter(true);
    try {
      const fn = httpsCallable(functions, 'rmsFetchTriageDocument', { timeout: 30000 });
      const result = await fn({
        orgId,
        patientRecordId,
        fileFormat: 'pdf',
        disposition: 'inline',
        ...(effPathway ? { pathway: effPathway } : {}),
      });
      const url = result.data?.downloadUrl;
      if (url) window.open(url, '_blank');
      else toast.error('The letter could not be fetched. Please try again.');
    } catch (err) {
      console.error('Letter fetch failed:', err);
      toast.error('The letter could not be fetched. Please try again.');
    } finally {
      setOpeningLetter(false);
    }
  }, [orgId, patientRecordId, effPathway, openingLetter]);

  const handleSignOff = useCallback(async () => {
    if (signingOff) return;
    setSigningOff(true);
    try {
      await apiSignOffTriageLetter({
        orgId,
        patientRecordId,
        pathway: effPathway || undefined,
      });
      toast.success('Letter signed off. It is now final; the admin team sends it from the Admin Handover screen.');
      setShowSignOffDialog(false);
      navigate(`/rms/provider/patient/${patientRecordId}${pathwayParam ? `?pathway=${pathwayParam}` : ''}`);
    } catch (err) {
      console.error('Failed to sign off triage letter:', err);
      toast.error(err?.message || 'Could not sign off the letter. Please try again.');
    } finally {
      setSigningOff(false);
    }
  }, [orgId, patientRecordId, effPathway, pathwayParam, navigate, signingOff]);

  const patientName = [patientData?.subject?.firstName, patientData?.subject?.lastName]
    .filter(Boolean).join(' ').trim();

  // ─────────────────────────────────────────────────────────────────────────
  // Side Panel: Render Input Categories
  // ─────────────────────────────────────────────────────────────────────────

  /**
   * A readable label for an input field key.
   *
   * Keys arrive as camelCase or snake_case identifiers. Shown raw they are
   * worse than nothing, so they are spaced out and sentence-cased; a key that
   * is already a human phrase is left alone.
   */
  const humaniseFieldKey = (key) => {
    const raw = String(key || '').trim();
    if (!raw) return '';
    if (/\s/.test(raw)) return raw;
    const spaced = raw
      .replace(/[_-]+/g, ' ')
      .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
      .toLowerCase()
      .trim();
    return spaced.charAt(0).toUpperCase() + spaced.slice(1);
  };

  /**
   * One input field's value. Several of these arrive as a single multi-line
   * string — the surfaced form evidence is one line per answer, grouped by
   * the factor it belongs to — and rendering that as one block made the
   * panel a wall of text: a clinician checking what the AI was given had to
   * read every line to find one answer (Eve, 27 October 2026). Multi-line
   * values are laid out as the structure they already are: a heading per
   * factor, then one answer at a time, each with the respondent who gave it.
   * Anything that does not parse is shown whole, so no evidence is lost.
   */
  // The controls on one piece of evidence (TDI-406), when the paragraph is
  // adjustable. `piece` is { category, fieldKey }; `index` is the line's
  // position among the field's non-empty lines, as the server counts them.
  const renderPieceControls = (piece, index, text) => {
    if (!piece || !canAdjustEvidence) return null;
    const key = evidencePieceKey(piece.category, piece.fieldKey, index);
    return (
      <EvidencePieceControls
        state={overrideState(selectedOverrides, key)}
        disabled={Boolean(revisingMode)}
        onChange={(state) => handleSetEvidenceOverride(selectedCode, key, text, state)}
      />
    );
  };

  const pieceClass = (piece, index) => {
    if (!piece) return '';
    const key = evidencePieceKey(piece.category, piece.fieldKey, index);
    return overrideState(selectedOverrides, key) === 'removed' ? 'line-through opacity-60' : '';
  };

  const renderFieldValue = (value, piece = null) => {
    const groups = parseEvidenceLines(value);
    if (!groups.length) {
      const text = splitEvidenceLines(value)[0] || '';
      return (
        <div>
          <p className={`mt-1 text-left text-sm text-gray-900 whitespace-pre-wrap ${pieceClass(piece, 0)}`}>{value}</p>
          {renderPieceControls(piece, 0, text)}
        </div>
      );
    }
    // Line index runs across the groups: the groups are the lines in order.
    let lineIndex = -1;
    return (
      <div className="mt-2 space-y-3">
        {groups.map((group, gi) => (
          <div key={`${group.factor || 'ungrouped'}-${gi}`}>
            {group.factor && (
              <p className="text-left text-xs font-semibold text-gray-700">{group.factor}</p>
            )}
            <div className={`space-y-1.5 ${group.factor ? 'mt-1.5' : ''}`}>
              {group.items.map((item, ii) => {
                lineIndex += 1;
                const index = lineIndex;
                return (
                  <div
                    key={`${item.raw}-${ii}`}
                    className="rounded-md border border-gray-200 bg-gray-50/70 px-2.5 py-2"
                  >
                    <div className={pieceClass(piece, index)}>
                      {item.question ? (
                        <>
                          <div className="flex items-start justify-between gap-2">
                            <p className="text-left text-xs text-gray-600">{item.question}</p>
                            {item.respondent && (
                              <span className="whitespace-nowrap rounded-full bg-white px-2 py-0.5 text-[11px] text-gray-600 border border-gray-200">
                                {item.respondent}
                              </span>
                            )}
                          </div>
                          <p className="mt-1 text-left text-sm text-gray-900">{item.answer}</p>
                        </>
                      ) : (
                        <p className="text-left text-sm text-gray-900 whitespace-pre-wrap">{item.answer}</p>
                      )}
                    </div>
                    {renderPieceControls(piece, index, item.raw)}
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    );
  };

  const renderInputCategory = (categoryName, fields) => {
    if (categoryName === 'Question that LLM answered') return null;
    if (!fields || typeof fields !== 'object') return null;

    const entries = Object.entries(fields).filter(([, f]) => {
      const val = typeof f === 'object' ? f?.value : f;
      return val && val.toString().trim();
    });

    if (entries.length === 0) return null;

    return (
      <div key={categoryName} className="mb-6">
        <h4 className="text-left text-xs font-semibold uppercase tracking-wide text-gray-500 pb-1 mb-2 border-b border-gray-200">
          {categoryName}
        </h4>
        <div className="space-y-3">
          {entries.map(([key, field]) => {
            const value = typeof field === 'object' ? field?.value : field;
            const source = typeof field === 'object' ? field?.source : null;

            return (
              <div key={key} className="rounded-lg border border-gray-200 bg-white p-3">
                {/* The field name was missing entirely, so the panel read as a
                    wall of values with no way to tell which question each one
                    answered (Eve, 28 Sept). */}
                <p className="text-left text-xs font-medium text-gray-500">{humaniseFieldKey(key)}</p>
                {renderFieldValue(value, { category: categoryName, fieldKey: key })}
                {source && (
                  <p className="mt-2 text-left text-xs text-gray-500">From {source}</p>
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  // ─────────────────────────────────────────────────────────────────────────
  // Side Panel: the structured outcome letter's inputs by who they came from
  // (TDI-698, R-017): "Your decisions", then "Informant provided
  // information" ("School provided", then "Parent or carer provided"), every
  // line tagged with its source. The TDI-406 controls stay on every line.
  // ─────────────────────────────────────────────────────────────────────────

  const roleTag = (role) => {
    const label = INPUT_ROLE_TAGS[role];
    if (!label) return null;
    const isDecision = role === 'clinician';
    return (
      <span
        data-testid="input-role-tag"
        className={`whitespace-nowrap rounded-full px-2 py-0.5 text-xs border ${
          isDecision ? 'bg-primary/10 text-primary border-primary/20' : 'bg-white text-gray-700 border-gray-300'
        }`}
      >
        {label}
      </span>
    );
  };

  const answerFromLine = (answerFrom) => (
    answerFrom && INPUT_ROLE_TAGS[answerFrom] ? (
      <p className="mt-1 text-left text-xs text-gray-500">Answer from {INPUT_ROLE_TAGS[answerFrom]}</p>
    ) : null
  );

  // One line (or one parsed answer) of an input field, with its tag, the
  // "Answer from" line on a tagged answer, and the evidence controls.
  const renderInputLine = (piece, item, { question = null, answer, key }) => (
    <div
      key={key}
      data-testid="input-line"
      className="rounded-md border border-gray-200 bg-gray-50/70 px-2.5 py-2"
    >
      <div className={pieceClass(piece, item.index)}>
        <div className="flex items-start justify-between gap-2">
          {question ? (
            <p className="text-left text-xs text-gray-600">{question}</p>
          ) : (
            <p className="text-left text-sm text-gray-900 whitespace-pre-wrap">{answer}</p>
          )}
          {roleTag(item.role)}
        </div>
        {question && <p className="mt-1 text-left text-sm text-gray-900">{answer}</p>}
        {answerFromLine(item.answerFrom)}
      </div>
      {renderPieceControls(piece, item.index, item.text ?? item.raw)}
    </div>
  );

  const INPUT_FIELD_LABELS = {
    evidence_tags: 'Answers you tagged',
    surfaced_factors: 'Answers related to each factor',
    snap_summary: 'SNAP-IV scores',
  };
  const inputFieldLabel = (fieldKey) => {
    const base = Object.keys(INPUT_FIELD_LABELS).find(
      (k) => fieldKey === k || String(fieldKey).startsWith(`${k}_`),
    );
    return base ? INPUT_FIELD_LABELS[base] : humaniseFieldKey(fieldKey);
  };

  const renderInputEntry = (entry) => {
    const piece = { category: entry.category, fieldKey: entry.fieldKey };
    // A one-line value is shown whole; several lines are laid out by factor,
    // one answer at a time, as before.
    const groups = entry.lines.length > 1 ? groupEvidenceLines(entry.lines) : null;
    return (
      <div key={`${entry.category}|${entry.fieldKey}`} className="rounded-lg border border-gray-200 bg-white p-3">
        <p className="text-left text-xs font-medium text-gray-500">{inputFieldLabel(entry.fieldKey)}</p>
        {groups ? (
          <div className="mt-2 space-y-3">
            {groups.map((group, gi) => (
              <div key={`${group.factor || 'ungrouped'}-${gi}`}>
                {group.factor && (
                  <p className="text-left text-xs font-semibold text-gray-700">{group.factor}</p>
                )}
                <div className={`space-y-1.5 ${group.factor ? 'mt-1.5' : ''}`}>
                  {group.items.map((item) => renderInputLine(piece, item, {
                    question: item.question,
                    answer: item.answer,
                    key: `${item.index}-${item.raw}`,
                  }))}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="mt-2">
            {renderInputLine(piece, entry.lines[0], { answer: entry.lines[0].text, key: 'only' })}
          </div>
        )}
        {entry.source && (
          <p className="mt-2 text-left text-xs text-gray-500">From {entry.source}</p>
        )}
      </div>
    );
  };

  const renderGroupedInputs = (paragraphInputs) => {
    const grouped = groupLetterInputs(paragraphInputs);
    const hasInformant = grouped.school.length > 0 || grouped.parent.length > 0;
    return (
      <>
        <section data-testid="inputs-group-decisions" className="mb-6">
          <h4 className="text-left text-sm font-semibold text-gray-900 pb-1 mb-2 border-b border-gray-200">
            {INPUT_GROUP_DECISIONS}
          </h4>
          {grouped.decisions.length > 0 ? (
            <div className="space-y-3">{grouped.decisions.map(renderInputEntry)}</div>
          ) : (
            <p className="text-left text-sm text-gray-600">
              You didn&apos;t record anything for this section. It&apos;s drafted from what the school
              and the family told us.
            </p>
          )}
        </section>
        {hasInformant && (
          <section data-testid="inputs-group-informant" className="mb-6">
            <h4 className="text-left text-sm font-semibold text-gray-900 pb-1 mb-2 border-b border-gray-200">
              {INFORMANT_GROUP_HEADING}
            </h4>
            {grouped.school.length > 0 && (
              <div data-testid="inputs-group-school" className="mb-4">
                <h5 className="text-left text-sm font-medium text-gray-700 mb-2">{INPUT_GROUP_SCHOOL}</h5>
                <div className="space-y-3">{grouped.school.map(renderInputEntry)}</div>
              </div>
            )}
            {grouped.parent.length > 0 && (
              <div data-testid="inputs-group-parent">
                <h5 className="text-left text-sm font-medium text-gray-700 mb-2">{INPUT_GROUP_PARENT}</h5>
                <div className="space-y-3">{grouped.parent.map(renderInputEntry)}</div>
              </div>
            )}
          </section>
        )}
      </>
    );
  };

  // ─────────────────────────────────────────────────────────────────────────
  // Document Section Component
  // ─────────────────────────────────────────────────────────────────────────

  const Section = ({ title, paragraphs }) => {
    const availableParagraphs = Object.entries(paragraphs).filter(
      ([, { code }]) => outputs[code] !== undefined && outputs[code] !== ''
    );

    if (availableParagraphs.length === 0) return null;

    return (
      <div className="mb-8">
        <h2 className="text-2xl font-bold mb-4 text-blue-500">{title}</h2>

        {availableParagraphs.map(([key, { title: subTitle, code }]) => {
          const content = outputs[code] || '';

          return (
            <div key={key} className="mb-4">
              {subTitle && (
                <h3 className="font-semibold mb-2 text-gray-900">{subTitle}</h3>
              )}
              <div
                data-paragraph-container
                id={code}
                className={`
                  relative text-sm font-medium p-2 rounded-lg
                  transition-all duration-200
                  ${isEditing && selectedCode === code
                    ? 'bg-amber-100 z-20'
                    : reviewedCodes.has(code)
                      ? 'bg-green-600/30 hover:bg-green-600/40'
                      : 'bg-violet-100 hover:bg-violet-200'
                  }
                  ${selectedCode === code ? 'ring ring-violet-800' : ''}
                  ${!isEditing ? 'cursor-pointer' : ''}
                `}
                onClick={(e) => {
                  if (!isEditing) handleParagraphClick(e, code, content);
                }}
              >
                <p
                  ref={editableContentRef}
                  className="text-left text-gray-700 leading-relaxed whitespace-pre-wrap"
                  contentEditable={isEditing && selectedCode === code}
                  suppressContentEditableWarning={true}
                  onKeyDown={suppressFormattingShortcuts}
                  onInput={() => {
                    // Do NOT push each keystroke into React state: re-rendering
                    // this contentEditable would rewrite its text node and reset
                    // the caret. The DOM owns the text while editing; it is read
                    // back on save/approve via readEditedContent().
                    if (isEditing && selectedCode === code) safeUpdatePosition(code);
                  }}
                  onPaste={handlePlainTextPaste}
                >
                  {content}
                </p>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  // ─────────────────────────────────────────────────────────────────────────
  // Letter title and breadcrumbs
  // ─────────────────────────────────────────────────────────────────────────

  const letterTitle = useMemo(() => {
    if (letterType === 'adhd_outcome_letter') return 'ADHD Outcome Letter';
    if (letterType === 'accepted_letter') return 'Referral Accepted Letter';
    if (letterType === 'discharged_letter') return 'Referral Not Accepted Letter';
    return 'Triage Letter Review';
  }, [letterType]);

  const breadcrumbs = useMemo(() => {
    // Carry the letter's pathway so a combined referral's overview and
    // documents dashboard keep showing THIS pathway's state after a
    // breadcrumb hop (dropping it fell back to the ADHD container).
    const q = effPathway ? `?pathway=${effPathway}` : '';
    return [
      { label: 'Home', path: '/rms/provider/dashboard' },
      { label: 'Patient Overview', path: `/rms/provider/patient/${patientRecordId}${q}` },
      { label: 'Documents', path: `/rms/provider/patient/${patientRecordId}/documents${q}` },
      { label: 'Review', path: null },
    ];
  }, [patientRecordId, effPathway]);

  // TDI-470 FR5: the family can withdraw consent while this screen is open.
  // Every server write is already refused, but the review stayed live and the
  // clinician met a raw refusal instead of being told. Watch the record the
  // way the proforma and the overview do, and stop on a withdrawal or on the
  // rules closing the record underneath.
  useEffect(() => {
    if (!orgId || !patientRecordId) return undefined;
    const patientRef = doc(db, 'rms_organisations', orgId, 'patientRecords', patientRecordId);
    return onSnapshot(
      patientRef,
      (snap) => { if (snap.exists()) setWithdrawalRecord(snap.data() || {}); },
      (err) => setWithdrawalListenerError(err),
    );
  }, [orgId, patientRecordId]);

  const withdrawnStop = useConsentWithdrawnStop({
    patientRecord: withdrawalRecord,
    listenerError: withdrawalListenerError,
  });

  if (withdrawnStop.stopped) {
    return (
      <div className="min-h-screen bg-white p-8">
        <ConsentWithdrawnDialog open onLeave={() => navigate('/rms/provider/dashboard')} />
      </div>
    );
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Loading State
  // ─────────────────────────────────────────────────────────────────────────

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen bg-gray-50">
        <div className="text-center">
          <div className="relative w-10 h-10 mb-3 mx-auto">
            <div className="absolute inset-0 border-4 border-primary/20 rounded-full" />
            <div className="absolute inset-0 border-4 border-transparent border-t-primary rounded-full animate-spin" />
          </div>
          <p className="text-sm text-gray-600">Loading letter review...</p>
        </div>
      </div>
    );
  }

  if (!letterType || Object.keys(outputs).length === 0) {
    return (
      <div className="flex items-center justify-center h-screen bg-gray-50">
        <div className="text-center max-w-md">
          <p className="text-lg font-semibold text-gray-800 mb-2">No letter to review</p>
          <p className="text-sm text-gray-600 mb-4">
            The triage letter has not been generated yet. Please wait for AI generation to complete.
          </p>
          <button
            type="button"
            onClick={() => navigate(`/rms/provider/patient/${patientRecordId}/documents${effPathway ? `?pathway=${effPathway}` : ''}`)}
            className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 text-sm font-medium"
          >
            Back to Documents
          </button>
        </div>
      </div>
    );
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Access control gate
  // ─────────────────────────────────────────────────────────────────────────
  // Only the clinician whose free-text input fed the letter (the "assigned
  // reviewer") may interactively review, edit, and approve paragraphs.
  // Other authenticated users see a read-only notice instead.

  const isAssignedReviewer = !assignedReviewerId || assignedReviewerId === auth.currentUser?.uid;
  // TDI-695: whether the signed-in clinician may change the decision this
  // letter is drafted from (theirs, and not signed off).
  const decisionChange = triageDecisionChange(patientData, effPathway, auth.currentUser?.uid);

  if (!isAssignedReviewer) {
    return (
      <div className="flex items-center justify-center h-screen bg-gray-50">
        <div className="text-center max-w-md">
          <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-primary/10 flex items-center justify-center">
            <svg className="w-7 h-7 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            </svg>
          </div>
          <p className="text-lg font-semibold text-gray-800 mb-2">Review assigned to another clinician</p>
          <p className="text-base text-gray-600 mb-4">
            {assignedReviewerName ? (
              <>
                This patient is assigned to <span className="font-semibold">{assignedReviewerName}</span>. Only the
                clinician named here can review and approve their triage outcome letter.
              </>
            ) : (
              'This patient is assigned to another clinician.'
            )}
          </p>
          {/* Back to the queue, where the letter is waiting on its reviewer:
              the patient's documents are a dead end for anyone else. The
              dashboard reopens on the pathway tab kept in session state. */}
          <button
            type="button"
            onClick={() => navigate('/rms/provider/dashboard')}
            className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 text-sm font-medium"
          >
            Back to dashboard
          </button>
        </div>
      </div>
    );
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Render
  // ─────────────────────────────────────────────────────────────────────────

  return (
    <div className="h-screen flex flex-col bg-gray-50">
      <RmsTopNavBar breadcrumbs={breadcrumbs} />
      {/* The child's identity, the same on every triage screen (TDI-392);
          this review carried none before. */}
      <ChildIdentityHeader patient={patientData} patientRecordId={patientRecordId} loading={isLoading && !patientData} />

      {/* Fixed header with progress and actions */}
      <div className="flex-shrink-0 pt-4">
        <DocumentReviewHeader
          title={letterTitle}
          reviewedCodes={effectiveReviewed}
          totalParagraphs={totalParagraphs}
          isSaving={isSaving}
          handleExitReview={handleExitReview}
          goToFirstUnreviewed={goToFirstUnreviewed}
          calculateProgress={calculateProgress}
        />
        {/* TDI-695: the deciding clinician may change their decision until
            the letter is signed off. The header itself is the shared
            Assessment component, so the action sits on its own row under it. */}
        {decisionChange.allowed && !isSignedOff && (
          <div className="mx-8 mt-2 flex justify-end">
            <ChangeDecisionButton
              orgId={orgId}
              patientRecordId={patientRecordId}
              pathway={effPathway}
              firstRejection={decisionChange.firstRejection}
              onReopened={() => navigate(`/rms/provider/patient/${patientRecordId}/triage${effPathway ? `?pathway=${effPathway}` : ''}`)}
            />
          </div>
        )}
      </div>

      {/* AI-generated content acknowledgement — shown once on entry. */}
      <InitialWarningDialog
        showInitialWarning={showInitialWarning}
        setShowInitialWarning={setShowInitialWarning}
      />

      {/* Warning bar about AI-generated content */}
      <WarningBar
        show={showWarning}
        message="Purple highlighted sections are AI-generated. Please review carefully as AI can make mistakes. You are responsible for verifying all content."
      />

      {/* Signed-off lock (TD-249): the letter is final — read-only surface. */}
      {isSignedOff && (
        <div className="mx-8 mt-2 flex items-start gap-3 rounded-lg border border-green-300 bg-green-50 px-4 py-3">
          <p className="text-sm text-green-800 text-left">
            This letter has been signed off by{' '}
            <strong>{triageDocuments?.signOff?.signedOffBy?.clinicianName || 'a clinician'}</strong>{' '}
            and is final. It can no longer be edited, regenerated or re-rendered.
          </p>
        </div>
      )}

      {/* Sign-off inside the review (TDI-395): every paragraph approved, the
          letter renders here, and sign-off is this screen's last action. The
          only other way out is to go back and edit. */}
      {signOffStage && !isSignedOff && (
        <div
          role="status"
          className="mx-8 mt-2 flex items-center justify-between gap-4 flex-wrap rounded-lg border border-violet-300 bg-violet-50 px-4 py-3"
        >
          <p className="text-sm text-violet-900 text-left flex items-center gap-2">
            {signOffStage === 'rendering' && (
              <svg className="animate-spin h-4 w-4 flex-none text-violet-800 motion-reduce:animate-none" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
              </svg>
            )}
            {signOffStage === 'rendering'
              ? 'Every paragraph is approved. The letter is being prepared from exactly that text, which takes about 30 seconds; sign-off opens as soon as it is ready.'
              : signOffStage === 'failed'
                ? 'The letter could not be prepared. Try again, or go back and edit.'
                : canSignOffHere
                ? 'The letter is ready. Check it, then sign it off, or go back and edit.'
                : `The letter is ready. Only ${assignedReviewerName || 'the clinician who reviewed it'} can sign it off.`}
          </p>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setSignOffStage(null)}
              className="rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              Go back and edit
            </button>
            {signOffStage === 'failed' && (
              <button
                type="button"
                onClick={retryRender}
                className="rounded-md bg-violet-800 px-3 py-1.5 text-sm font-semibold text-white hover:bg-violet-700"
              >
                Try again
              </button>
            )}
            {signOffStage === 'ready' && (
              <button
                type="button"
                onClick={handleOpenLetter}
                disabled={openingLetter}
                className="rounded-md border border-violet-700 bg-white px-3 py-1.5 text-sm font-medium text-violet-800 hover:bg-violet-100 disabled:opacity-50"
              >
                View letter
              </button>
            )}
            {signOffStage === 'ready' && canSignOffHere && (
              <button
                type="button"
                onClick={() => setShowSignOffDialog(true)}
                disabled={signingOff}
                className="rounded-md bg-violet-800 px-3 py-1.5 text-sm font-semibold text-white hover:bg-violet-700 disabled:opacity-50"
              >
                Sign off letter
              </button>
            )}
          </div>
        </div>
      )}
      <SignOffTriageLetterDialog
        open={showSignOffDialog}
        onOpenChange={setShowSignOffDialog}
        patientName={patientName}
        signing={signingOff}
        onConfirm={handleSignOff}
      />

      {/* Main content area: split panel */}
      <div className="flex-grow flex overflow-hidden px-8 pb-4">
        {/* Left panel: Document paragraphs */}
        <div
          ref={documentContainerRef}
          className="relative w-3/5 overflow-y-auto pr-4"
          style={{ position: 'relative' }}
        >
          {Object.entries(docStructure).map(([sectionTitle, paragraphs]) => (
            <Section key={sectionTitle} title={sectionTitle} paragraphs={paragraphs} />
          ))}

          {/* Floating action panel (positioned by the hook) */}
          <FloatingActionPanel
            panelRef={panelRef}
            position={panelPosition}
            visible={!!selectedCode && !isSignedOff}
            isEditing={isEditing}
            hasBeenModified={hasBeenModified(selectedCode)}
            onStartEdit={handleStartEdit}
            onSave={handleSaveEdit}
            onCancel={handleCancel}
            onReset={handleReset}
            onApprove={handleApprove}
            onSkip={handleSkip}
          />
        </div>

        {/* Right panel: Structured inputs for the selected paragraph */}
        <div className="w-2/5 overflow-y-auto pl-4 border-l border-gray-200">
          {/* AI revision of the selected paragraph: an instruction the AI
              revises from, or a tidy of the clinician's own edit. Both come
              back as a proposal the clinician reads before it can replace the
              paragraph. */}
          {selectedCode && !isEditing && !isSignedOff && outputs[selectedCode] && (
            <ParagraphRevisionPanel
              canPolish={canPolishSelected}
              evidenceAdjusted={canAdjustEvidence && evidenceAdjustedText ? evidenceAdjustedText : null}
              onRegenerateFromEvidence={handleRegenerateFromEvidence}
              busyMode={revisingMode}
              proposal={proposal && proposal.code === selectedCode ? proposal : null}
              onSuggest={handleSuggestChanges}
              onPolish={handlePolish}
              onAcceptProposal={handleAcceptProposal}
              onDiscardProposal={handleDiscardProposal}
            />
          )}
          {/* Per-section regenerate (structured ADHD outcome letter only). Rewrites
              the selected section from its recorded inputs; the section returns
              unapproved and must be re-approved. */}
          {/* Once the evidence has been adjusted, the redraft above (a proposal
              from the adjusted evidence) replaces this, which would overwrite
              the section from the unadjusted inputs. */}
          {isStructuredOutcomeLetter && selectedCode && !isEditing && !isSignedOff && !evidenceAdjustedText && (
            <div className="p-4 pb-0">
              <button
                type="button"
                data-testid="regenerate-section-btn"
                onClick={handleRegenerate}
                disabled={regeneratingCode === selectedCode}
                className="w-full px-4 py-2 rounded-lg text-sm font-medium border border-primary/40 text-primary hover:bg-primary/10 disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {regeneratingCode === selectedCode ? 'Regenerating…' : 'Regenerate this section'}
              </button>
              <p className="mt-2 text-xs text-left text-gray-500">
                Rewrites this section from the recorded inputs. It will need to be reviewed and
                approved again.
              </p>
            </div>
          )}
          {sidePanelInputs ? (
            <div className="p-4">
              <h3 className="text-base font-semibold text-gray-900">
                What this paragraph was written from
              </h3>
              {isStructuredOutcomeLetter && (
                <p className="mt-1 text-sm text-gray-600">
                  Your decisions come first. What the school and the family told us supports them.
                </p>
              )}
              <p className="mt-1 mb-5 text-sm text-gray-600">
                Every answer the AI was given for this section, and where each came from.
              </p>
              {isStructuredOutcomeLetter
                ? renderGroupedInputs(sidePanelInputs)
                : Object.entries(sidePanelInputs).map(([category, fields]) =>
                  renderInputCategory(category, fields)
                )}
            </div>
          ) : !selectedCode ? (
            <div className="flex items-center justify-center h-full text-gray-400">
              <div className="text-center">
                <p className="text-sm font-medium">Click a paragraph to view its inputs</p>
                <p className="text-xs mt-1">
                  The inputs shown here are what the AI used to generate the paragraph
                </p>
              </div>
            </div>
          ) : null}
          {/* How this paragraph reached its current wording (TDI-408). Shown
              for a signed-off letter too: the trail is part of the audit. */}
          {selectedCode && (
            <ParagraphHistory
              history={editHistory[selectedCode]}
              paragraphTitle={paragraphTitles[selectedCode]}
            />
          )}
        </div>
      </div>
    </div>
  );
}
